import { useEffect, useRef } from "react";
import { useMutation } from "@apollo/client";

import { USER_RECORD_ACTIVITY } from "graphql/mutation/user-gql";

const ACTIVITY_EVENTS = [ "mousemove", "keydown", "click", "scroll", "touchstart" ] as const;
const ACTIVITY_INTERVAL_MS = 50000;

interface IUseUserRecordActivityProps
{
    enabled: boolean;
}

const useUserRecordActivity = ({ enabled }: IUseUserRecordActivityProps): void =>
{
    const hasActivityRef = useRef(false);
    const isRecordingRef = useRef(false);
    const lastRecordAttemptAtRef = useRef(0);
    const timeoutIdRef = useRef<number | null>(null);
    const [ recordActivity ] = useMutation(USER_RECORD_ACTIVITY);

    useEffect(() =>
    {
        if (!enabled) {
            hasActivityRef.current = false;
            lastRecordAttemptAtRef.current = 0;
            return;
        }

        let isActive = true;

        const clearRecordTimeout = () =>
        {
            if (timeoutIdRef.current === null) {
                return;
            }

            window.clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        };

        const getRemainingInterval = () =>
        {
            if (!lastRecordAttemptAtRef.current) {
                return 0;
            }

            return Math.max(ACTIVITY_INTERVAL_MS - (Date.now() - lastRecordAttemptAtRef.current), 0);
        };

        const scheduleRecordActivity = (delay: number) =>
        {
            if (timeoutIdRef.current !== null) {
                return;
            }

            timeoutIdRef.current = window.setTimeout(() =>
            {
                timeoutIdRef.current = null;
                flushActivity();
            }, delay);
        };

        const flushActivity = async () =>
        {
            if (!hasActivityRef.current) {
                return;
            }

            if (document.visibilityState !== "visible") {
                return;
            }

            if (!navigator.onLine) {
                return;
            }

            if (isRecordingRef.current) {
                return;
            }

            const remainingInterval = getRemainingInterval();

            if (remainingInterval > 0) {
                scheduleRecordActivity(remainingInterval);
                return;
            }

            clearRecordTimeout();
            isRecordingRef.current = true;
            lastRecordAttemptAtRef.current = Date.now();
            hasActivityRef.current = false;

            try {
                await recordActivity();
            } catch {
                if (isActive) {
                    hasActivityRef.current = true;
                }
            } finally {
                if (isActive) {
                    isRecordingRef.current = false;

                    if (hasActivityRef.current) {
                        flushActivity();
                    }
                }
            }
        };

        const markActivity = () =>
        {
            hasActivityRef.current = true;
            flushActivity();
        };

        const handleVisibilityChange = () =>
        {
            if (document.visibilityState === "visible") {
                flushActivity();
            }
        };

        ACTIVITY_EVENTS.forEach((eventName) =>
        {
            window.addEventListener(eventName, markActivity, { passive: true });
        });

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("online", flushActivity);

        return () =>
        {
            isActive = false;
            ACTIVITY_EVENTS.forEach((eventName) =>
            {
                window.removeEventListener(eventName, markActivity);
            });
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("online", flushActivity);
            clearRecordTimeout();
            hasActivityRef.current = false;
            isRecordingRef.current = false;
            lastRecordAttemptAtRef.current = 0;
        };
    }, [ enabled, recordActivity ]);
};

export default useUserRecordActivity;
